import "./modules/menu.js";
import "./modules/accordion.js";
import "./modules/tabs.js";
import "./modules/fancybox.js";
import {
  initHousesSwiper,
  initHouseGallerySlider,
} from "./modules/houses-swiper.js";
import { initSimulationAnimation } from "./modules/line-animation.js";
import { initTechSwiper } from "./modules/tech-swiper.js";
import {
  initGutenbergSwiper,
  initGutenbergColumnSwiper,
  initGutenbergSwiperTechnology,
  initGutenbergLogosMarquee,
  initAccordionBlobWrapperSwiper,
  initHomeMobileAccordionSwiper,
  initTechNavMobileSwiper,
} from "./modules/gb-swiper.js";

import Swiper from "swiper";
import { Navigation, Pagination, EffectFade, Autoplay } from "swiper/modules";
Swiper.use([Navigation, Pagination, EffectFade, Autoplay]);
window.SwiperGlobal = Swiper.default || Swiper;

import { gsap } from "gsap";
import "splitting/dist/splitting.css";
import "splitting/dist/splitting-cells.css";
import Splitting from "splitting";

import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);
window.gsap = gsap;
window.Splitting = Splitting;

document.addEventListener("DOMContentLoaded", () => {
  initSimulationAnimation();
  initHousesSwiper();
  initAccordionBlobWrapperSwiper();
  initHouseGallerySlider();
  initTechSwiper();
  initGutenbergSwiper();
  initGutenbergSwiperTechnology();
  initGutenbergColumnSwiper();
  initGutenbergLogosMarquee();
  initSwiperSlidesReveal();
  initAppleStyleMenuReveal();
  initHomeMobileAccordionSwiper();
  initTechNavMobileSwiper();
});

const initAppleStyleMenuReveal = () => {
  const menuContainer = document.querySelector(".fixed-menu-container");
  if (!menuContainer) return;

  const menuSections = menuContainer.querySelectorAll(
    '.fixed-menu-container > div[class*="col-"]',
  );

  gsap.set(menuContainer, {
    clipPath: "inset(50% 0% 50% 0%)",
    backgroundColor: "rgba(255, 255, 255, 0)",
    backdropFilter: "blur(0px)",
    boxShadow: "0 0 0 0 rgba(0, 0, 0, 0)",
    opacity: 1,
    y: -5,
  });

  gsap.set(menuSections, {
    opacity: 0,
    y: 20,
  });

  const tl = gsap.timeline({
    delay: 0.2,
    defaults: { ease: "expo.out" },
  });

  tl.to(menuContainer, {
    clipPath: "inset(0% 0% 0% 0%)",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    backdropFilter: "blur(3px)",
    duration: 0.6,
    onComplete: () => {
      gsap.set(menuContainer, { clearProps: "clipPath" });
    },
  })
    .to(
      menuContainer,
      {
        boxShadow: "0 18px 34.2px 0 rgba(0, 0, 0, 0.15)",
        y: 0,
        duration: 1.8,
        ease: "power3.out",
      },
      "-=0.2",
    )
    .to(
      menuSections,
      {
        opacity: 1,
        y: 0,
        duration: 1.2,
        stagger: 0.2,
        ease: "power3.out",
      },
      "-=1.6",
    );
};

const initSwiperSlidesReveal = () => {
  const swiperContainers = document.querySelectorAll(".swiper");

  swiperContainers.forEach((swiperEl) => {
    const slides = swiperEl.querySelectorAll(".swiper-slide");

    if (slides.length === 0) return;

    gsap.set(slides, {
      opacity: 0,
      y: 30,
      scale: 1,
    });

    ScrollTrigger.create({
      trigger: swiperEl,
      start: "top bottom-=100",
      onEnter: () => {
        gsap.to(slides, {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.5,
          ease: "power3.out",
          stagger: 0.3,
          overwrite: true,
        });
      },
      once: true,
    });
  });
};

const initAnimations = () => {
  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined")
    return;
  gsap.registerPlugin(ScrollTrigger);

  document.querySelectorAll(".anim-image-reveal").forEach((el) => {
    if (el.dataset.animated === "true") return;

    let container = el;

    if (el.tagName === "IMG") {
      const wrapper = document.createElement("div");
      wrapper.className = "anim-image-reveal-wrapper " + el.className;
      el.parentNode.insertBefore(wrapper, el);
      wrapper.appendChild(el);
      container = wrapper;
    }

    container.style.overflow = "hidden";

    if (!container.querySelector(".scaler")) {
      const content = container.innerHTML;
      container.innerHTML = `<div class="scaler" style="display:flex; width:100%; height:100%;">${content}</div>`;
    }

    const scaler = container.querySelector(".scaler");
    el.dataset.animated = "true";

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: container,
        start: "top 65%",
        toggleActions: "play none none none",
        once: true,
      },
    });

    tl.set(container, { visibility: "visible" }).fromTo(
      scaler,
      {
        scale: 1.4,
        clipPath: "inset(0 0 0 100%)",
        webkitClipPath: "inset(0 0 0 100%)",
      },
      {
        scale: 1,
        clipPath: "inset(0 0 0 0%)",
        webkitClipPath: "inset(0 0 0 0%)",
        duration: 2.2,
        ease: "power4.out",
        force3D: true,
        immediateRender: false,
        clearProps: "will-change",
      },
    );
  });

  document.querySelectorAll(".text-animation").forEach((trigger) => {
    if (trigger.parentElement.classList.contains("text-animation-wrapper"))
      return;

    const wrapper = document.createElement("div");
    wrapper.className = "text-animation-wrapper";
    trigger.parentNode.insertBefore(wrapper, trigger);
    wrapper.appendChild(trigger);

    gsap.fromTo(
      trigger,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: wrapper,
          start: "top bottom-=50",
          toggleActions: "play none none none",
          once: true,
        },
      },
    );
  });

  if (typeof Splitting !== "undefined") {
    document.querySelectorAll(".anim-text-reveal").forEach((el) => {
      if (el.classList.contains("splitting")) return;

      const by = el.dataset.animBy || "chars";

      Splitting({ target: el, by: by });

      const targets =
        by === "chars"
          ? el.querySelectorAll(".char")
          : el.querySelectorAll(".word");

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top bottom-=100",
          toggleActions: "play none none none",
          once: true,
          onEnter: () => {
            gsap.set(el, { visibility: "visible" });
          },
        },
      });

      tl.from(targets, {
        yPercent: 110,
        autoAlpha: 1,
        ease: "power3.out",
        duration: 0.8,
        stagger: 0.02,
      });
    });
  } else {
    console.warn(
      "Splitting.js nie jest załadowany! Animacje tekstu nie zadziałają.",
    );
  }
};

window.addEventListener("load", initAnimations);

if (window.acf) {
  window.acf.addAction("render_block_preview", () => {
    setTimeout(initAnimations, 500);
  });
}
